import React, { useEffect, useState } from 'react';

import cx from 'classnames';

import styles from './loader.module.scss';

interface LoaderProps {
  fixed?: boolean;
  small?: boolean;
  className?: string;
  delayBeforeShow?: number;
  centered?: boolean;
}

const SMALL_SCALE_MULTIPLIER = 0.5;

/**
 * Usage/customisation:
 *
 * - `fixed` (boolean): Use when you want the loader to cover the entire viewport (e.g. global data fetches).
 *   Applies `position: fixed` and centers the spinner.
 *
 * - `centered` (boolean): Use when you want to center the loader inside its parent container,
 *   without fixing it to the viewport. Uses margin:auto. Good for inline sections like panels or sub-pages.
 *
 * - `small` (boolean): Use when you need a compact loader. It applies a scale transform
 *   so the spinner takes up less space. Useful for small UI elements such as in labels/form validation.
 *
 * - `delayBeforeShow` (number, default 500ms): Time in milliseconds before the loader is rendered.
 *   Helps avoid showing the spinner for very short operations where a flash would feel jarring.
 */
export default function Loader({ fixed, centered, small, className, delayBeforeShow = 500 }: LoaderProps) {
  const [visible, setVisible] = useState(!delayBeforeShow);

  useEffect(() => {
    if (!delayBeforeShow) {
      return;
    }

    const timeout = setTimeout(() => {
      setVisible(true);
    }, delayBeforeShow);

    return () => clearTimeout(timeout);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <div
      className={cx(styles['loader-container'], className, {
        [styles['loader-container--fixed']]: fixed,
        'm-auto': centered,
      })}
      style={small ? { transform: `scale(${SMALL_SCALE_MULTIPLIER})` } : {}}
      data-testid="loader"
    >
      <span className="sr-only">Loading</span>
      <div className={styles.loader}>
        <div className={styles['loader__line']}>
          <div className={styles['loader__cog']}>
            <div className={cx(styles['loader__cog-inner'], styles['loader__cog-inner--left'])} />
          </div>
          <div className={styles['loader__ticker']}>
            <div className={cx(styles['loader__cog-inner'], styles['loader__cog-inner--center'])} />
          </div>
          <div className={styles['loader__cog']}>
            <div className={cx(styles['loader__cog-inner'], styles['loader__cog-inner--right'])} />
          </div>
        </div>
      </div>
    </div>
  );
}
